/**
 * Extraction pipeline prompts — ARCHITECTURE.md section 6.3.
 *
 * Each agent prompt follows the architecture's four rules:
 * 1. Exact role in one sentence
 * 2. Output schema with field descriptions
 * 3. "Return null, do not guess" for uncertainty
 * 4. No commentary, summaries, or meta-text outside the schema
 */

import type { EntityType, RelationshipType } from '@/intelligence/entities/types';
import type { PriorityIndicator, OntologyExtensions, DeskConfig } from '@/config/desk-config';

// Re-export Zod schemas for consumers that import from this module
export { RelevanceResultSchema } from '../schemas/relevance';
export { EntityExtractionResultSchema, ExtractedEntitySchema } from '../schemas/entities';
export { RelationshipResultSchema } from '../schemas/relationships';
export { DraftAssessmentSchema } from '../schemas/synthesis';
export { ScopeContractSchema } from '../schemas/decomposition';

export interface PromptPair {
  system: string;
  user: string;
}

// ---------------------------------------------------------------------------
// Relevance filter — Haiku (fast, cheap, first gate)
// ---------------------------------------------------------------------------

export function buildRelevanceFilterPrompt(
  text: string,
  indicators: PriorityIndicator[],
): PromptPair {
  const indicatorList = indicators
    .map(pi => `- [${pi.id}] (${pi.priority}) ${pi.description}`)
    .join('\n');

  const system =
    'You are a relevance scoring agent. Your sole task is to score how relevant a piece of text is to a set of intelligence priority indicators.\n\n' +
    'Output JSON only. Schema:\n' +
    '{\n' +
    '  "score": <number 0.0-1.0>,\n' +
    '  "matchedIndicatorIds": [<string IDs of matched indicators>]\n' +
    '}\n\n' +
    'Rules:\n' +
    '- score: 0.0 = completely irrelevant, 1.0 = directly addresses multiple indicators\n' +
    '- matchedIndicatorIds: only include IDs of indicators the text clearly relates to\n' +
    '- If no indicators match, return {"score": 0.0, "matchedIndicatorIds": []}\n' +
    '- Return ONLY the JSON object. No commentary.';

  const user =
    'PRIORITY INDICATORS:\n' +
    indicatorList +
    '\n\nTEXT TO SCORE:\n' +
    text;

  return { system, user };
}

// ---------------------------------------------------------------------------
// Entity extractor — Sonnet (structured extraction)
// ---------------------------------------------------------------------------

export function buildEntityExtractionPrompt(
  text: string,
  entityTypes: EntityType[],
  ontologyExtensions?: OntologyExtensions,
): PromptPair {
  const typeList = entityTypes.join(', ');

  let ontologyNote = '';
  if (ontologyExtensions) {
    const subtypes = ontologyExtensions.assetSubtypes;
    if (subtypes?.length) {
      ontologyNote = `\nAsset subtypes: ${subtypes.join(', ')}`;
    }
  }

  const system =
    'You are an OSINT entity extraction agent. Your sole task is to identify named entities in source text and return them as structured JSON.\n\n' +
    'Output JSON only. Schema:\n' +
    '{\n' +
    '  "entities": [\n' +
    '    {\n' +
    '      "type": "<one of: ' + typeList + '>",\n' +
    '      "name": "<canonical entity name>",\n' +
    '      "description": "<one-sentence description or null>",\n' +
    '      "aliases": ["<alternative names>"],\n' +
    '      "properties": {"<domain-specific fields>"},\n' +
    '      "extractionQuote": "<exact verbatim quote from source text where this entity appears>",\n' +
    '      "confidence": <number 0.0-1.0>\n' +
    '    }\n' +
    '  ]\n' +
    '}\n\n' +
    'Allowed entity types: ' + typeList + ontologyNote + '\n\n' +
    'Rules:\n' +
    '- extractionQuote MUST be an EXACT substring of the source text. Copy it character-for-character.\n' +
    '- If you cannot find a verbatim quote for an entity, do NOT include that entity.\n' +
    '- confidence: your certainty that this entity is correctly identified (0.0-1.0)\n' +
    '- For ASSET entities: include asset_type and identifiers (MMSI, IMO, ICAO24) in properties when available\n' +
    '- For LOCATION entities: include coordinates [lat, lng] in properties when available\n' +
    '- Return null for any field you are uncertain about. Do not guess.\n' +
    '- If no entities are found, return {"entities": []}\n' +
    '- Return ONLY the JSON object. No commentary, no explanation.';

  const user = 'SOURCE TEXT:\n' + text;

  return { system, user };
}

// ---------------------------------------------------------------------------
// Relationship identifier — Sonnet (relationship mapping)
// ---------------------------------------------------------------------------

export function buildRelationshipPrompt(
  entities: Array<{ name: string; type: EntityType }>,
  context: string,
  allowedTypes: RelationshipType[],
): PromptPair {
  const entityList = entities
    .map(e => `- ${e.name} (${e.type})`)
    .join('\n');

  const typeList = allowedTypes.join(', ');

  const system =
    'You are an OSINT relationship identification agent. Your sole task is to identify relationships between known entities based on context text.\n\n' +
    'Output JSON only. Schema:\n' +
    '{\n' +
    '  "relationships": [\n' +
    '    {\n' +
    '      "sourceEntity": "<name of source entity>",\n' +
    '      "targetEntity": "<name of target entity>",\n' +
    '      "type": "<one of: ' + typeList + '>",\n' +
    '      "confidence": <number 0.0-1.0>,\n' +
    '      "evidenceQuote": "<exact verbatim quote from context supporting this relationship>"\n' +
    '    }\n' +
    '  ]\n' +
    '}\n\n' +
    'Allowed relationship types: ' + typeList + '\n\n' +
    'Rules:\n' +
    '- sourceEntity and targetEntity MUST be names from the provided entity list\n' +
    '- type MUST be one of the allowed relationship types listed above\n' +
    '- evidenceQuote MUST be an EXACT substring of the context text\n' +
    '- If you cannot find evidence for a relationship, do NOT include it\n' +
    '- Do not infer relationships not supported by the text. Return null, do not guess.\n' +
    '- If no relationships are found, return {"relationships": []}\n' +
    '- Return ONLY the JSON object. No commentary.';

  const user =
    'KNOWN ENTITIES:\n' +
    entityList +
    '\n\nCONTEXT TEXT:\n' +
    context;

  return { system, user };
}

// ---------------------------------------------------------------------------
// Synthesis agent — Opus (draft assessment generation)
// ---------------------------------------------------------------------------

export function buildSynthesisPrompt(
  graphSummary: string,
  template: string,
): PromptPair {
  const system =
    'You are an intelligence synthesis agent. Your sole task is to generate a structured draft assessment from validated entity and relationship data.\n\n' +
    'Output JSON only. Schema:\n' +
    '{\n' +
    '  "executiveSummary": "<3-5 sentence summary of key findings>",\n' +
    '  "sections": [\n' +
    '    {\n' +
    '      "title": "<section title>",\n' +
    '      "content": "<section content>",\n' +
    '      "citations": ["<entity IDs referenced in this section>"]\n' +
    '    }\n' +
    '  ]\n' +
    '}\n\n' +
    'Rules:\n' +
    '- Every factual claim MUST cite at least one entity ID from the graph data\n' +
    '- Use only information present in the provided graph data. Do not add external knowledge.\n' +
    '- Flag information gaps explicitly: "No data available for X"\n' +
    '- Assessment is a MACHINE-GENERATED DRAFT requiring analyst review\n' +
    '- Be precise, professional, and concise\n' +
    '- Return ONLY the JSON object. No commentary.';

  const user =
    'ASSESSMENT TEMPLATE:\n' +
    template +
    '\n\nVALIDATED GRAPH DATA:\n' +
    graphSummary;

  return { system, user };
}

// ---------------------------------------------------------------------------
// Query decomposition — Sonnet (scope contract from free text)
// ---------------------------------------------------------------------------

export function buildQueryDecompositionPrompt(
  rawQuery: string,
  deskConfig: DeskConfig,
): PromptPair {
  const sourceList = deskConfig.sources
    .filter(s => s.enabled)
    .map(s => `- ${s.name} (${s.type}, reliability: ${s.reliability})`)
    .join('\n');

  const indicatorList = deskConfig.priorityIndicators
    .map(pi => `- [${pi.id}] (${pi.priority}) ${pi.description}`)
    .join('\n');

  const watchlistList = deskConfig.entityWatchlists
    .map(w => `- ${w.name} (${w.type})${w.aliases?.length ? ` aliases: ${w.aliases.join(', ')}` : ''}`)
    .join('\n');

  const bbox = deskConfig.geographicFocus.primary.coords;

  const system =
    'You are an OSINT query decomposition agent. Your sole task is to parse an analyst\'s free-text investigation query into a structured scope contract.\n\n' +
    'Output JSON only. Schema:\n' +
    '{\n' +
    '  "subject": {\n' +
    '    "type": "<entity type: PERSON, ORGANIZATION, LOCATION, ASSET, EVENT, INDICATOR, SOURCE>",\n' +
    '    "subtype": "<specific subtype or null>",\n' +
    '    "description": "<one-sentence description of what we are looking for>",\n' +
    '    "watchlistMatches": ["<names from watchlist that match, or empty array>"]\n' +
    '  },\n' +
    '  "behaviorIndicators": [\n' +
    '    {"type": "<indicator type>", "threshold": "<threshold description>"}\n' +
    '  ],\n' +
    '  "geographicBounds": {\n' +
    '    "source": "desk_config_primary_aoi",\n' +
    '    "bbox": [' + bbox.join(', ') + '],\n' +
    '    "override": false\n' +
    '  },\n' +
    '  "temporalBounds": {\n' +
    '    "start": "<ISO 8601 timestamp>",\n' +
    '    "end": "<ISO 8601 timestamp>",\n' +
    '    "source": "desk_config_default_7d"\n' +
    '  },\n' +
    '  "exclusions": ["<what this investigation is NOT about>"]\n' +
    '}\n\n' +
    'Rules:\n' +
    '- subject.type MUST be one of: PERSON, ORGANIZATION, LOCATION, ASSET, EVENT, INDICATOR, SOURCE\n' +
    '- geographicBounds MUST use the desk config bounding box: [' + bbox.join(', ') + ']\n' +
    '- temporalBounds default to the last 7 days from now unless the query specifies otherwise\n' +
    '- Match watchlist entries if the query references them\n' +
    '- Behavior indicators should be specific and measurable\n' +
    '- Exclusions should clarify what is NOT in scope\n' +
    '- Return null for any field you are uncertain about. Do not guess.\n' +
    '- Return ONLY the JSON object. No commentary.';

  const user =
    'ANALYST QUERY: "' + rawQuery + '"\n\n' +
    'DESK CONFIG CONTEXT:\n' +
    'Name: ' + deskConfig.name + '\n' +
    'Geographic focus: bbox [' + bbox.join(', ') + ']\n\n' +
    'AVAILABLE SOURCES:\n' + sourceList + '\n\n' +
    'PRIORITY INDICATORS:\n' + indicatorList + '\n\n' +
    'WATCHLIST:\n' + (watchlistList || '(none)') + '\n\n' +
    'Current time: ' + new Date().toISOString();

  return { system, user };
}
